import React, { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Typography,
  Divider,
  Card,
  Spin,
  message,
  Modal,
  Button,
} from "antd";
import {
  ShopOutlined,
  ContactsOutlined,
  DeleteOutlined,
  UserAddOutlined,
  UsergroupAddOutlined,
} from "@ant-design/icons";
import {
  API_URL_PERMISSIONS_PARTICIPATION,
} from "@/services/XCONS_ENV";
import { axiosInstance } from "@/utilities";
import { StoreList } from "../drag-list";
import { t } from "i18next";
import { NormalModal } from "../modals/NormalModal";
import { RelationsContactsCreate } from "./relations-contacts-create";
import { SellerIcon, RelationIcon } from "../icon";
import { useOperatorContacts, filterOutOperators } from "@/hooks/useOperatorContacts";

const { Text } = Typography;

interface RelationCardProps {
  apiData: any;
  contactId?: number;
  identity?: any;
  // "Agregar" ahora vive en el header del collapse (contactDetails). El modal
  // de crear relación se controla desde afuera vía addOpen/onCloseAdd.
  addOpen?: boolean;
  onCloseAdd?: () => void;
  // #144: se dispara tras agregar/quitar una relación, para que el padre refresque el
  // contactRecord (y el contador de "Relaciones", que lee participations.length).
  onRelationsChanged?: () => void;
}

export const RelationsCard: React.FC<RelationCardProps> = ({
  apiData,
  contactId,
  identity,
  addOpen,
  onCloseAdd,
  onRelationsChanged,
}) => {
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<any>([]);

  // Hide the technical operator service-account that Magento attaches via
  // adminLogin flow (config api/general/customer_email). All downstream
  // groupings derive from `visibleParticipants` instead of raw state.
  const operatorContactIds = useOperatorContacts();
  const visibleParticipants: any[] = useMemo(
    () => filterOutOperators<any>(participants ?? [], operatorContactIds),
    [participants, operatorContactIds],
  );

  const getParticipants = async () => {
    if (!contactId) {
      setParticipants([]);
      return;
    }
    setLoading(true);
    try {
      // Endpoint returns participations grouped by permission code:
      //   { contact: [...] }
      // RelationsCard expects a flat array, so we unwrap.
      const response = await axiosInstance.get(
        `${API_URL_PERMISSIONS_PARTICIPATION}/entity/${contactId}`,
        { params: { codes: "contact" } },
      );
      const grouped = response.data || {};
      const flat = Array.isArray(grouped.contact) ? grouped.contact : [];
      setParticipants(flat);
    } catch (error) {
      console.error("Error al obtener participantes:", error);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  // #144: refetch de la lista interna + aviso al padre (refresca contactRecord → contador).
  // Se usa tras mutaciones (agregar/quitar); el fetch inicial usa getParticipants directo.
  const refresh = async () => {
    await getParticipants();
    onRelationsChanged?.();
  };

  useEffect(() => {
    getParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const handleUpdateParticipant = async (
    newList: any[],
    identifier: string,
    type: string,
  ) => {
    setLoading(true);
    try {
      const updates = newList
        .filter((item) => {
          const serverParticipant = participants.find((p: any) => p.id === item.id);
          const serverPosition = serverParticipant?.position ?? item.raw.position;
          const serverRole = serverParticipant?.value ?? item.raw.value;

          const changedRole = item.role !== serverRole;
          const changedPosition = item.position !== serverPosition;
          return changedRole || changedPosition;
        })
        .map((item) => ({
          contactId: item.raw.contactId,
          permissionCode: "contact",
          entityId: item.raw.entityId,
          participationType: type,
          participationIdentifier: identifier,
          value: item.role,
          position: item.position,
        }));

      if (updates.length > 0) {
        await Promise.all(
          updates.map((payload) =>
            axiosInstance.post(API_URL_PERMISSIONS_PARTICIPATION, payload),
          ),
        );
        message.success("Participantes actualizados");
      } else {
        message.info("No hay cambios para guardar");
        return;
      }
      refresh();
    } catch (error: any) {
      console.error("Error al guardar participante", error);
      message.error("No se pudo guardar el participante");
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerAction = (id: number, action: string) => {
    if (action !== "remove") return;
    Modal.confirm({
      title: "¿Estas seguro que deseas eliminar esta relación?",
      okText: "Eliminar",
      cancelText: "Cancelar",
      okType: "danger",
      onOk: async () => {
        try {
          await axiosInstance.delete(`${API_URL_PERMISSIONS_PARTICIPATION}/${id}`);
          message.success("Participante eliminado");
          refresh();
        } catch (error: any) {
          console.error("Error al eliminar participante", error);
          if (error?.message === "You do not have permission") {
            message.error("No tienes permiso para eliminar este participante");
          } else {
            message.error("No se pudo eliminar el participante");
          }
        }
      },
    });
  };

  // ── Permisos de acceso ────────────────────────────────────────────────
  const canSee = (v: any) => {
    const s = String(v ?? "").trim().toLowerCase();
    return s !== "" && s !== "none" && s !== "no_access";
  };

  const targetStoreCode = String(apiData?.storeCode ?? "").trim();

  const storeContactMap: Record<string, string> =
    identity?.permissions?.store?.contact ?? {};

  /**
   * Los sellers de los que el operador ES operador — pertenencia, no nivel.
   *
   * Antes esto leía el valor de `seller_permission` y lo pasaba por `canSee`. Pero ese código
   * gobierna el panel de Operadores del Seller: apagarlo le escondía en la ficha los sellers a los
   * que pertenece. La pertenencia son las CLAVES del bucket —el backend puebla los seis códigos de
   * tipo seller para cada fila de permisos— y ningún `no_access` la borra.
   */
  const sellerMembership: Set<string> = useMemo(() => {
    const codes = new Set<string>();
    for (const bucket of Object.values(identity?.permissions?.seller ?? {})) {
      for (const code of Object.keys((bucket as Record<string, string>) ?? {})) {
        codes.add(code);
      }
    }
    return codes;
  }, [identity?.permissions?.seller]);

  const hasStoreAccess = canSee(storeContactMap[targetStoreCode]);

  // ── Sellers a mostrar ─────────────────────────────────────────────────
  const sellerCodesFromPerms = useMemo(() => {
    const set = new Set<string>();
    const rawPerms = apiData?.permissions;
    if (Array.isArray(rawPerms)) {
      for (const p of rawPerms) {
        if (p?.seller) set.add(String(p.seller).trim());
      }
    } else if (rawPerms && typeof rawPerms === "object") {
      if (Array.isArray(rawPerms.seller)) {
        rawPerms.seller.forEach((s: any) => {
          const c = String(s?.code ?? s ?? "").trim();
          if (c) set.add(c);
        });
      }
    }
    return set;
  }, [apiData?.permissions]);

  // Fuente 2: participants ya guardados con type="seller"
  const sellerCodesFromParticipants = useMemo(() => {
    const set = new Set<string>();
    for (const p of visibleParticipants) {
      const rawType = (p?.participationType ?? "").trim();
      const rawId = (p?.participationIdentifier ?? "").trim();
      if (rawType === "seller" && rawId) set.add(rawId);
    }
    return set;
  }, [visibleParticipants]);

  // Unión ordenada de ambas fuentes
  const allSellerCodes = useMemo(() => {
    const merged = new Set<string>([...sellerCodesFromPerms, ...sellerCodesFromParticipants]);
    return Array.from(merged).sort();
  }, [sellerCodesFromPerms, sellerCodesFromParticipants]);

  // ── Participantes agrupados ───────────────────────────────────────────
  const storeParticipantsList = useMemo(
    () => visibleParticipants.filter(
      (p: any) => p?.contact?.isActive && (p?.participationType ?? "").trim() === "store"
    ),
    [visibleParticipants],
  );

  const customerParticipantsList = useMemo(
    () => visibleParticipants.filter(
      (p: any) => p?.contact?.isActive && (p?.participationType ?? "").trim() === ""
    ),
    [visibleParticipants],
  );

  const sellerParticipantsByCode = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const p of visibleParticipants) {
      if (!p?.contact?.isActive) continue;
      if ((p?.participationType ?? "").trim() !== "seller") continue;
      const code = (p?.participationIdentifier ?? "").trim();
      if (!code) continue;
      if (!map[code]) map[code] = [];
      map[code].push(p);
    }
    return map;
  }, [visibleParticipants]);

  const getNextPosition = (type: string, identifier: string) => {
    const filtered = visibleParticipants.filter(
      (p: any) =>
        (p.participationType ?? "").trim() === type &&
        (p.participationIdentifier ?? "").trim() === identifier,
    );
    if (!filtered.length) return 0;
    
    // Some participants might not have position saved, in that case we use their index + 1 or max found
    const maxPosition = Math.max(
      ...filtered.map((p: any, index: number) => {
         if (typeof p.position === 'number') return p.position;
         return index; // fallback si no tenían position definida
      })
    );
    
    return maxPosition + 1;
  };

  // ── Sin acceso ────────────────────────────────────────────────────────
  if (!targetStoreCode || !hasStoreAccess) {
    return (
      <Text type="secondary" italic>
        No tienes permisos para crear una relación.
      </Text>
    );
  }

  const hasAnyParticipant =
    storeParticipantsList.length > 0 ||
    customerParticipantsList.length > 0 ||
    Object.keys(sellerParticipantsByCode).length > 0;

  return (
    <Spin spinning={loading}>

      {/* Modal de crear relación, controlado desde el header del collapse. */}
      <NormalModal
        title={t("contacts.label.add.relation")}
        icon={<UsergroupAddOutlined />}
        footer={null}
        destroyOnClose
        open={!!addOpen}
        onCancel={() => onCloseAdd?.()}
        styles={{ content: { minHeight: 520 } }}
      >
        <RelationsContactsCreate
          t={t}
          sectionType="contact"
          contactId={contactId}
          refetch={refresh}
          participants={participants}
          contactStores={{ ...apiData, storeCode: targetStoreCode }}
          nextPosition={getNextPosition("store", targetStoreCode)}
        />
      </NormalModal>

      {/* ── Sin ningún participante ──────────────────────
          Único empty-state: sale solo cuando no hay relaciones visibles
          (store/customer/seller). Los sellers sin participantes ya no
          renderizan su propio texto. */}
      {!hasAnyParticipant && (
        <Text type="secondary" italic>
          {t("contacts.contact.doesn't.relation.yet")}
        </Text>
      )}

       {/* ── Customers ──────────────────────────────────── */}
      {customerParticipantsList.length > 0 && (
        <Card
          className="drag-list-card"
          style={{ marginTop: 12 }}
          title={
            <Flex gap={6} align="center">
              <ContactsOutlined />
              <Text strong>{t("quotes.status.Customer")}</Text>
            </Flex>
          }
        >
          {customerParticipantsList.map((p: any) => (
            <Flex key={p.id} gap={8} className="customer-item" justify="space-between">
              <Text>{`${p.contact?.user?.firstname} ${p.contact?.user?.lastname}`}</Text>
              <Button
                size="small"
                type="text"
                icon={<DeleteOutlined />}
                onClick={(e) => { e.stopPropagation(); handleCustomerAction(p.id, "remove"); }}
              />
            </Flex>
          ))}
        </Card>
      )}

      {/* ── Store operators ────────────────────────────── */}
      {storeParticipantsList.length > 0 && (
        <StoreList
          icon
          type="store"
          key="store-operators"
          title={
            <Flex align="center" gap={8}>
              <ShopOutlined />
              <Text>{t("smartMenu.storeLabel.storeOperators")}</Text>
            </Flex>
          }
          participants={storeParticipantsList.map((p: any) => ({
            id: p.id,
            name: `${p.contact?.user?.firstname} ${p.contact?.user?.lastname}`,
            role: p.value,
            position: p.position,
            raw: p,
          }))}
          onChange={(newList) => handleUpdateParticipant(newList, targetStoreCode, "store")}
          getParticipants={refresh}
        />
      )}

      {/* ── Sellers: uno por sellerCode ────────────────── */}
      {allSellerCodes.map((sellerCode) => {
        const sellerParts = sellerParticipantsByCode[sellerCode] ?? [];
        const hasSellerAccess =
          sellerMembership.has(sellerCode) ||
          canSee(storeContactMap[targetStoreCode]);

        if (!hasSellerAccess) return null;

        // Un seller sin participantes no aporta nada visualmente. Antes se
        // mostraba "El contacto aún no tiene relación." por cada seller vacío,
        // lo que confundía cuando el contacto SÍ tenía relaciones de cliente.
        if (sellerParts.length === 0) return null;

        return (
          <Flex key={`seller-${sellerCode}`} vertical >
            <StoreList
              icon
              type="seller"
              key={`seller-list-${sellerCode}`}
              title={<Flex align="center" gap={8}><SellerIcon />Seller: {sellerCode}</Flex>}
              participants={sellerParts.map((p: any) => ({
                id: p.id,
                name: `${p.contact?.user?.firstname} ${p.contact?.user?.lastname}`,
                role: p.value,
                position: p.position,
                raw: p,
              }))}
              onChange={(newList) => handleUpdateParticipant(newList, sellerCode, "seller")}
              getParticipants={refresh}
            />
          </Flex>
        );
      })}

      {/* <Divider style={{ margin: "12px 0" }} /> */}

    </Spin>
  );
};
